import {
  importLibrary,
  setOptions as setLoaderOptions,
} from '@googlemaps/js-api-loader';
import { LocationOn, Search } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface ParsedGoogleAddress {
  address1: string;
  city: string;
  state: string;
  postcode: string;
  latitude: number | null;
  longitude: number | null;
}

interface Prediction {
  placeId: string;
  description: string;
  mainText: string;
  secondaryText: string;
}

interface GoogleAddressSearchProps {
  apiKey: string;
  onSelect: (address: ParsedGoogleAddress) => void;
  disabled?: boolean;
}

/**
 * Parse a Google Places `address_components` array into our flat address shape.
 * @param components - Array of address components returned by the Places API
 * @returns Parsed address with address1, city, state, and postcode fields
 */
function parseAddressComponents(
  components: google.maps.GeocoderAddressComponent[],
): Omit<ParsedGoogleAddress, 'latitude' | 'longitude'> {
  const get = (
    type: string,
    nameType: 'long_name' | 'short_name' = 'long_name',
  ) => components.find((c) => c.types.includes(type))?.[nameType] ?? '';

  const streetNumber = get('street_number');
  const route = get('route');

  return {
    address1: [streetNumber, route].filter(Boolean).join(' '),
    city: get('locality') || get('sublocality') || get('neighborhood'),
    // State should be the abbreviated form (e.g. "IA" not "Iowa")
    state: get('administrative_area_level_1', 'short_name'),
    postcode: get('postal_code'),
  };
}

export function GoogleAddressSearch({
  apiKey,
  onSelect,
  disabled = false,
}: GoogleAddressSearchProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<Prediction[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const autocompleteService =
    useRef<google.maps.places.AutocompleteService | null>(null);
  const placesService = useRef<google.maps.places.PlacesService | null>(null);
  // PlacesService requires a DOM element — we attach it to a hidden div
  const placesRef = useRef<HTMLDivElement>(null);

  // Load the Google Maps JS API with the Places library
  useEffect(() => {
    setLoaderOptions({ key: apiKey, v: 'weekly' });

    importLibrary('places')
      .then((places) => {
        autocompleteService.current = new places.AutocompleteService();
        if (placesRef.current) {
          placesService.current = new places.PlacesService(placesRef.current);
        }
        setIsLoaded(true);
      })
      .catch(() => {
        // Google Maps failed to load — address search will remain disabled
      });
  }, [apiKey]);

  const fetchPredictions = useMemo(
    () =>
      debounce((query: string) => {
        if (!autocompleteService.current || query.length < 3) {
          setOptions([]);
          setIsSearching(false);
          return;
        }

        autocompleteService.current.getPlacePredictions(
          {
            input: query,
            types: ['address'],
            componentRestrictions: { country: 'us' },
          },
          (predictions, status) => {
            setIsSearching(false);
            if (
              status === google.maps.places.PlacesServiceStatus.OK &&
              predictions
            ) {
              setOptions(
                predictions.map((p) => ({
                  placeId: p.place_id,
                  description: p.description,
                  mainText: p.structured_formatting.main_text,
                  secondaryText: p.structured_formatting.secondary_text,
                })),
              );
            } else {
              setOptions([]);
            }
          },
        );
      }, 300),
    [],
  );

  const handleInputChange = useCallback(
    (_: unknown, value: string) => {
      setInputValue(value);
      if (value.length >= 3) {
        setIsSearching(true);
      }
      fetchPredictions(value);
    },
    [fetchPredictions],
  );

  const handleSelect = useCallback(
    (_: unknown, value: string | Prediction | null) => {
      // freeSolo can yield a raw string when the user types without picking a suggestion
      if (!value || typeof value === 'string' || !placesService.current) return;

      placesService.current.getDetails(
        {
          placeId: value.placeId,
          fields: ['address_components', 'geometry'],
        },
        (place, status) => {
          if (
            status === google.maps.places.PlacesServiceStatus.OK &&
            place?.address_components
          ) {
            const parsed = parseAddressComponents(place.address_components);
            const lat = place.geometry?.location?.lat() ?? null;
            const lng = place.geometry?.location?.lng() ?? null;

            onSelect({ ...parsed, latitude: lat, longitude: lng });
            // Clear the search field after selection
            setInputValue('');
            setOptions([]);
          }
        },
      );
    },
    [onSelect],
  );

  // Cleanup debounce on unmount
  useEffect(() => () => fetchPredictions.cancel(), [fetchPredictions]);

  return (
    <>
      {/* Hidden div required by PlacesService */}
      <div ref={placesRef} style={{ display: 'none' }} />

      <Autocomplete
        freeSolo
        filterOptions={(x) => x} // Let Google handle filtering
        options={options}
        getOptionLabel={(opt) =>
          typeof opt === 'string' ? opt : opt.description
        }
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onChange={handleSelect}
        disabled={disabled || !isLoaded}
        loading={isSearching}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Search address"
            placeholder="Start typing an address…"
            slotProps={{
              input: {
                ...params.InputProps,
                startAdornment: (
                  <Search
                    sx={{ color: 'text.secondary', mr: 0.5, fontSize: 20 }}
                  />
                ),
                endAdornment: isSearching ? (
                  <CircularProgress size={16} />
                ) : (
                  params.InputProps.endAdornment
                ),
              },
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box
            component="li"
            {...props}
            key={typeof option === 'string' ? option : option.placeId}
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
          >
            <LocationOn
              sx={{ color: 'text.secondary', mt: 0.5, flexShrink: 0 }}
            />
            <Box>
              <Typography variant="body2" fontWeight={500}>
                {typeof option === 'string' ? option : option.mainText}
              </Typography>
              {typeof option !== 'string' && option.secondaryText && (
                <Typography variant="caption" color="text.secondary">
                  {option.secondaryText}
                </Typography>
              )}
            </Box>
          </Box>
        )}
      />
    </>
  );
}
